// Mémoire des faits qui n'arrivent QUE par le temps réel.
//
// ## Le défaut que ce module corrige
// Le §0 du contrat pose une règle juste : « un événement ne dit jamais ce qu'il
// faut afficher, il dit "quelque chose a changé, va regarder" ». Elle suppose
// qu'on PUISSE aller regarder.
//
// Deux faits échappent à cette règle, parce qu'aucune route REST ne les rend :
//
//   • `photo_media_id` — la photo du colis au retrait. Portée par
//     `trip.awaiting.client.approval` seul ; ni `Trip` ni `TripStop` ne
//     déclarent le moindre champ média (vérifié sur les deux openapi.json,
//     docs-technique.html et tous les contrats).
//
//   • le verdict de versement — `stop.payment.released` / `refused`, avec le
//     motif écrit par l'opérateur. Aucun champ de `TripStop` ne le porte.
//
// Pour ces deux-là, l'événement N'EST PAS un signal : c'est la seule source. Un
// socket coupé, un onglet rouvert, un téléphone que le navigateur a mis en
// veille — et l'information est perdue DÉFINITIVEMENT. Le client ne verra
// jamais la photo du colis sur lequel il s'engage ; la Main ne saura jamais
// qu'un versement lui a été refusé, ni pourquoi.
//
// ## Pourquoi le navigateur, et pas mieux
// Parce qu'il n'y a pas mieux. Ce n'est pas l'architecture qu'on choisirait :
// la vraie correction est côté serveur — exposer ces champs sur
// `GET /trips/{id}`. Tant qu'elle n'existe pas, garder la valeur près de l'œil
// qui l'a vue est la seule chose que l'application puisse faire seule.
//
// C'est donc un PALLIATIF assumé, et il en a les limites : il ne franchit ni
// l'appareil, ni le navigateur, ni une navigation privée refermée. Un client
// qui ouvre le suivi sur un autre téléphone ne verra toujours rien. On ne
// prétend pas le contraire à l'écran.

/**
 * Préfixe des clés. `sosmain.` est la convention du dépôt (jetons, PKCE,
 * identifiant d'agent, pays préféré) ; `rt:` isole ce qui vient du temps réel,
 * pour qu'un vidage ciblé reste possible sans toucher à la session.
 */
const PREFIX = "sosmain.rt:";

/**
 * Durée de vie. Ces faits sont opérationnels et courts : une validation se joue
 * en minutes, un versement en secondes — et, quand il tarde, l'alerte de
 * non-réponse part à trente minutes. Vingt-quatre heures couvre très largement,
 * et borne ce qu'on garde d'une personne sur son appareil.
 */
const TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Nombre maximal d'entrées conservées.
 *
 * Sans plafond, une Main qui enchaîne les courses accumulerait indéfiniment —
 * et `localStorage` répondrait un jour par une exception de quota, au pire
 * moment : pendant l'écriture d'un verdict de refus. On élague donc à chaque
 * écriture, du plus ancien au plus récent.
 */
const MAX_ENTRIES = 40;

type Envelope = { v: unknown; at: number };

function fullKey(key: string): string {
  return `${PREFIX}${key}`;
}

/**
 * Toutes nos entrées, datées. Sert à l'élagage.
 *
 * Toute clé illisible est traitée comme périmée : une entrée qu'on ne sait plus
 * relire n'a aucune valeur, et la garder ferait vivre un déchet jusqu'au TTL.
 */
function ourEntries(): { key: string; at: number }[] {
  const out: { key: string; at: number }[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(PREFIX)) continue;
    let at = 0;
    try {
      at = (JSON.parse(localStorage.getItem(k) ?? "") as Envelope)?.at ?? 0;
    } catch {
      at = 0; // illisible → à jeter en premier
    }
    out.push({ key: k, at });
  }
  return out;
}

/**
 * @param reserve Places à laisser libres pour l'écriture qui suit.
 *
 * Sans cette réserve, l'élagage ramenait à `MAX_ENTRIES` puis l'écriture en
 * ajoutait une — le plafond était dépassé d'une unité à chaque tour, et la
 * seule borne réelle redevenait le quota du navigateur.
 */
function prune(now: number, reserve = 0): void {
  const entries = ourEntries();

  for (const e of entries) {
    if (now - e.at > TTL_MS) {
      try {
        localStorage.removeItem(e.key);
      } catch {
        /* stockage bloqué : rien à faire, et rien à signaler */
      }
    }
  }

  const budget = Math.max(0, MAX_ENTRIES - reserve);
  const alive = entries.filter((e) => now - e.at <= TTL_MS);
  if (alive.length <= budget) return;

  alive.sort((a, b) => a.at - b.at); // le plus ancien d'abord
  for (const e of alive.slice(0, alive.length - budget)) {
    try {
      localStorage.removeItem(e.key);
    } catch {
      /* idem */
    }
  }
}

/**
 * Retient une valeur reçue du temps réel.
 *
 * ⚠️ Best effort, TOUJOURS. Un stockage indisponible — navigation privée,
 * quota plein, réglage qui bloque les données de site — ne doit jamais faire
 * échouer l'affichage de l'événement qu'on vient de recevoir. On perd la
 * mémoire, pas l'instant présent.
 */
export function rememberEvent(key: string, value: unknown): void {
  if (value == null) return;
  try {
    const now = Date.now();
    // Une place réservée pour l'entrée qu'on s'apprête à écrire.
    prune(now, 1);
    localStorage.setItem(fullKey(key), JSON.stringify({ v: value, at: now } satisfies Envelope));
  } catch {
    /* dégradation silencieuse, comme partout ailleurs dans ce dépôt */
  }
}

/**
 * Relit une valeur retenue, ou `null`.
 *
 * Une entrée périmée est SUPPRIMÉE et rendue comme absente : mieux vaut ne rien
 * afficher qu'afficher un verdict d'hier comme s'il valait pour aujourd'hui.
 */
export function recallEvent<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(fullKey(key));
    if (!raw) return null;

    const env = JSON.parse(raw) as Envelope;
    if (!env || typeof env.at !== "number") return null;

    if (Date.now() - env.at > TTL_MS) {
      localStorage.removeItem(fullKey(key));
      return null;
    }
    return (env.v as T) ?? null;
  } catch {
    return null; // illisible ou stockage bloqué : on n'a rien, c'est tout
  }
}

/**
 * Oublie une valeur.
 *
 * À appeler dès que le fait a cessé d'être vrai — la course a quitté l'état de
 * validation, le versement est consommé. Garder au-delà ferait ressurgir un
 * bandeau résolu au montage suivant.
 */
export function forgetEvent(key: string): void {
  try {
    localStorage.removeItem(fullKey(key));
  } catch {
    /* rien à faire */
  }
}

// ─── Clés ────────────────────────────────────────────────────────────────────
// Centralisées : deux endroits qui composeraient la même clé à la main
// finiraient par diverger d'un tiret, et le défaut serait invisible — on
// écrirait quelque part, on relirait ailleurs, et rien ne reviendrait jamais.

/** Photo du colis présentée au client pour validation, arrêt par arrêt. */
export function proofPhotoKey(tripId: string, stopIndex: number): string {
  return `proof-photo:${tripId}:${stopIndex}`;
}

/**
 * Dernier récapitulatif de solde renvoyé par `approve` / `adjust`.
 *
 * Il n'arrive que dans la RÉPONSE d'une mutation — aucune route ne le relit.
 * C'est la même famille de défaut que les deux ci-dessus, mais par la surface
 * des mutations plutôt que par celle du socket.
 */
export function approvalSummaryKey(tripId: string, stopIndex: number): string {
  return `approval-summary:${tripId}:${stopIndex}`;
}

/**
 * Relevé du marchand déjà enregistré pour un arrêt (nom + numéro).
 *
 * Encore un fait que le contrat ne rend pas : `TripStop` ne déclare aucun champ
 * marchand, et `GET /trips/{id}` ne dit donc pas si le relevé a eu lieu.
 *
 * Ce n'est pas un confort ici, c'est ce qui rend un BLOCAGE tenable : sans
 * mémoire, un simple rechargement de page rebloquerait une Main qui vient
 * pourtant de renseigner la boutique, et l'empêcherait d'avancer sa course.
 */
export function stopMerchantKey(tripId: string, stopIndex: number): string {
  return `stop-merchant:${tripId}:${stopIndex}`;
}

/**
 * Nombre de Mains ayant reçu la même offre (`wave_size`).
 *
 * Encore un fait que seul l'événement connaît : `GET /trips` ne dit pas à
 * combien de Mains une course a été proposée. Or c'est ce nombre qui permet de
 * dire honnêtement à la Main qu'elle n'est pas seule dessus — et donc de faire
 * lire un refus d'acceptation comme une course disputée plutôt qu'un incident.
 */
export function offerWaveKey(tripId: string): string {
  return `offer-wave:${tripId}`;
}

/** Dernier verdict de versement reçu pour une course. */
export function stopPaymentKey(tripId: string): string {
  return `stop-payment:${tripId}`;
}

/**
 * Dernière issue de retrait reçue par la Main.
 *
 * Le fait à retenir n'est pas le statut — `GET /agent-payouts/mine` le redonne —
 * mais la phrase `consequence`, qui n'existe QUE dans l'événement : elle dit à
 * la Main que ses gains restent disponibles après un refus ou un échec. Le
 * document ordonne de l'afficher telle quelle ; sans mémoire, un rechargement
 * la remplacerait par notre propre formulation de repli.
 */
export function agentPayoutKey(payoutId: string): string {
  return `agent-payout:${payoutId}`;
}
